import { Controller } from 'stimulus'

/**
 * Manages js for the short incomes on the borrower and coborrower income steps
 *
 * @class ShortIncomeController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'id',
    'incomeType',
    'yearlyIncome',
    'error'
  ]

  /**
   * @function Controllers.ShortIncomeController.debounceSubmit
   * @memberof Controllers
   * @description An action that checks if the form already has an Income.id and
   * determines if it should submit a create (POST) or update (PATCH) request
   *
   * @return {undefined}
   *
   */
  debounceSubmit() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.idTarget.value ? this._updateIncome() : this._submitIncome()
    }, 500)
  }

  /**
   * @function Controllers.ShortIncomeController._submitIncome
   * @memberof Controllers
   * @description Submits a create (POST) request to the borrower_incomes_controller,
   * then sets the returned id to the hidden id field if create request is successful.
   * @private
   *
   * @return {undefined}
   *
   */
  async _submitIncome() {
    const data = this._dataFromIncome()
    const result = await fetch('/v1/borrower_incomes', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify(data)
    })

    if (result.status === 201) {
      const newIncome = await result.json()
      const newIncomeId = newIncome['id']
      this.idTarget.value = newIncomeId
    }

    this._maybeThrowErrors(result)
  }

  /**
   * @function Controllers.ShortIncomeController._updateIncome
   * @memberof Controllers
   * @description Submits an update (PATCH) request to the borrower_incomes_controller.
   * @private
   *
   * @return {undefined}
   *
   */
  async _updateIncome() {
    const data = this._dataFromIncome()
    const result = await fetch(`/v1/borrower_incomes/${this.idTarget.value}`, {
      method:      'PATCH',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify(data)
    })

    this._maybeThrowErrors(result)
  }

  /**
   * @function Controllers.ShortIncomeController._dataFromIncome
   * @memberof Controllers
   * @description Creates an object of data from the form targets
   * @private
   *
   * @return {object} an income object ready to be JSON.stringified
   *
   */
  _dataFromIncome() {
    const income = {}
    income['borrower_id'] = this.data.get('borrower')
    income['yearly_income'] = this.yearlyIncomeTarget.value
    income['income_type'] = this.incomeTypeTarget.value
    return income
  }

  /**
   * @function Controllers.ShortIncomeController._maybeThrowErrors
   * @memberof Controllers
   * @description If the result is a 422 and yearly_income is too large display its error
   * @private
   *
   * @param {object} result - ajax result
   */
  async _maybeThrowErrors(result) {
    if (result.status === 422) {
      const json  = await result.json()

      this._removeError()
      if (!json.errors['yearly_income'].includes('can\'t be blank')) {
        this._addError(json.errors)
      }

      this._nextVisibleEvent(false)
    } else {
      this._removeError()
      this._nextVisibleEvent(true)
    }
  }

  /**
   * @function Controllers.ShortIncomeController._addError
   * @memberof Controllers
   * @description Add errors to their respective input fields
   * @private
   *
   * @param {object} errors
   */
  _addError(errors) {
    const fieldName = this.yearlyIncomeTarget.dataset.fieldName
    if (errors[fieldName]) {
      this._removeError()
      this.yearlyIncomeTarget.dispatchEvent(new CustomEvent('spawnErrors', { detail: errors[fieldName] }))
    }
  }

  /**
   * @function Controllers.ShortIncomeController._removeError
   * @memberof Controllers
   * @description Remove errors from input fields
   * @private
   */
  _removeError() {
    this.errorTargets.forEach(errorField => {
      const input = errorField.parentElement.querySelector('input')
      input.dispatchEvent(new CustomEvent('removeErrors'))
    })
  }

  /**
   * @function Controllers.ShortIncomeController._nextVisibleEvent
   * @memberof Controllers
   * @description Toggle visibility of next button
   * @private
   * @param {boolean} shouldItBeVisible
   */
  _nextVisibleEvent(shouldItBeVisible) {
    this.yearlyIncomeTarget.dispatchEvent(new CustomEvent('nextVisible', { bubbles: true, detail: { toggle: shouldItBeVisible } }))
  }
}
